import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { AuthGuard, PermissionGuard, RoleGuard, AiFeatureGuard } from '@/auth';
import { AppShell } from '@/components/layout/AppShell';
import { Spinner } from '@/components/ui';
import { ROUTES } from './routes';
import { useTranslation } from '@/i18n';

// Login page (NetDocuments iframe integration)
const LoginPage = lazy(() => import('@/auth/LoginPage'));

// Lazy-loaded feature pages
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const AiAssistantPage = lazy(() => import('@/features/ai-assistant/AiAssistantPage'));
const MessagesPage = lazy(() => import('@/features/messages/MessagesPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const AdminPage = lazy(() => import('@/features/admin/AdminPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));
const SyncedDataPage = lazy(() => import('@/features/synced-data/SyncedDataPage'));
const BulkImportPage = lazy(() => import('@/features/synced-data/bulk-import/BulkImportPage'));
const EntityRecordsPage = lazy(() => import('@/features/entity-master/EntityRecordsPage'));
const EntityTypesPage = lazy(() => import('@/features/entity-master/EntityTypesPage'));
const EntityRecordBulkImportPage = lazy(() => import('@/features/entity-master/EntityRecordBulkImportPage'));
const TenantsPage = lazy(() => import('@/features/admin/tenants/TenantsPage'));
const RolesPage = lazy(() => import('@/features/roles/RolesPage'));
const DataSyncPage = lazy(() => import('@/features/data-sync/DataSyncPage'));
const PeopleWorkgroupsPage = lazy(() => import('@/features/people-workgroups/PeopleWorkgroupsPage'));
const UserImportPage = lazy(() => import('@/features/people-workgroups/UserImportPage'));
const MyAccountPage = lazy(() => import('@/features/my-account/MyAccountPage'));
const MyApprovalsPage = lazy(() => import('@/features/my-approvals/MyApprovalsPage'));
const AuthorityDelegationPage = lazy(() =>
  import('@/features/authority-delegation/AuthorityDelegationPage').then(m => ({
    default: m.AuthorityDelegationPage,
  }))
);
const StartNewJobPage = lazy(() => import('@/features/start-new-job/StartNewJobPage'));
const AddNewRecordPage = lazy(() => import('@/features/start-new-job/AddNewRecordPage'));
const NewServiceRequestPage = lazy(() => import('@/features/new-service-request/NewServiceRequestPage'));

// Client Dashboard (client-facing workspace)
const ClientMyRequestScreen = lazy(() => import('@/features/client-dashboard/screens/MyRequestScreen'));
const ClientFrontDoorScreen = lazy(() => import('@/features/client-dashboard/screens/FrontDoorScreen'));
const ClientDocumentsScreen = lazy(() => import('@/features/client-dashboard/screens/DocumentsScreen'));
const ClientActivityScreen = lazy(() => import('@/features/client-dashboard/screens/ActivityScreen'));

// Process Templates
const ProcessTemplatesPage = lazy(() =>
  import('@/features/process-templates').then((m) => ({ default: m.ProcessTemplatesPage }))
);
const TemplateTypesPage = lazy(() =>
  import('@/features/process-templates').then((m) => ({ default: m.TemplateTypesPage }))
);
const TemplateImportPage = lazy(() =>
  import('@/features/process-templates').then((m) => ({ default: m.TemplateImportPage }))
);

// Dashboard sub-pages
const WIPScreen = lazy(() => import('@/features/dashboard/screens/WIPScreen'));
const WorkloadScreen = lazy(() => import('@/features/dashboard/screens/WorkloadScreen'));
const SummaryScreen = lazy(() => import('@/features/dashboard/screens/SummaryScreen'));
const CalendarScreen = lazy(() => import('@/features/dashboard/screens/CalendarScreen'));
const QuestionnairesScreen = lazy(() => import('@/features/dashboard/screens/QuestionnairesScreen'));
const DashboardDefaultRedirect = lazy(() => import('@/features/dashboard/screens/DashboardDefaultRedirect'));
const ActivitiesScreen = lazy(() => import('@/features/dashboard/screens/ActivitiesScreen'));
const DashboardQuestionnaireEmbed = lazy(() => import('@/features/dashboard/screens/DashboardQuestionnaireEmbed'));

// DocuSign callback (handles redirect after signing ceremony)
const DocuSignCallbackScreen = lazy(
  () => import('@/features/dashboard/screens/DocuSignCallbackScreen')
);

// AI Management pages
const AiProvidersPage = lazy(() => import('@/features/ai-providers/AiProvidersPage'));
const AiModelsPage = lazy(() => import('@/features/ai-models/AiModelsPage'));
const AiTasksPage = lazy(() => import('@/features/ai-tasks/AiTasksPage'));
const KnowledgeBasePage = lazy(() => import('@/features/knowledge-base/KnowledgeBasePage'));

// AI Skills Library pages (B1)
const SkillsListPage = lazy(() =>
  import('@/features/skills').then((m) => ({ default: m.SkillsListPage }))
);
const SkillEditorPage = lazy(() =>
  import('@/features/skills').then((m) => ({ default: m.SkillEditorPage }))
);

// MCP Servers pages (B2)
const McpServersListPage = lazy(() =>
  import('@/features/mcp-servers').then((m) => ({ default: m.McpServersListPage }))
);
const McpServerEditorPage = lazy(() =>
  import('@/features/mcp-servers').then((m) => ({ default: m.McpServerEditorPage }))
);

// Packs pages (B3)
const PacksListPage = lazy(() =>
  import('@/features/packs').then((m) => ({ default: m.PacksListPage }))
);
const PackEditorPage = lazy(() =>
  import('@/features/packs').then((m) => ({ default: m.PackEditorPage }))
);
const PackEnablementPage = lazy(() =>
  import('@/features/packs').then((m) => ({ default: m.PackEnablementPage }))
);

// Template management pages
const SnippetsPage = lazy(() => import('@/features/snippets/SnippetsPage'));
const KeyMapsPage = lazy(() => import('@/features/key-maps/KeyMapsPage'));
const OutcomesPage = lazy(() => import('@/features/outcomes/OutcomesPage'));
const TagsPage = lazy(() => import('@/features/tags/TagsPage'));
const EmailTemplateConfigPage = lazy(() => import('@/features/email-template-config/EmailTemplateConfigPage'));
const EmailTemplatesPage = lazy(() => import('@/features/email-templates/EmailTemplatesPage'));
const SuggestedImprovementsPage = lazy(() => import('@/features/suggested-improvements/SuggestedImprovementsPage'));
const JobRisksReportPage = lazy(() => import('@/features/job-risks/JobRisksReportPage'));
const DocumentTemplatesPage = lazy(() => import('@/features/document-templates/DocumentTemplatesPage'));

// Power BI Reports
const PowerBIReportsPage = lazy(() => import('@/features/reports/PowerBIReportsPage'));
const PowerBIReportViewerPage = lazy(() => import('@/features/reports/PowerBIReportViewerPage'));

// Custom Reports
const UserReportPage = lazy(() => import('@/features/reports/UserReportPage'));
const ActivityReportPage = lazy(() => import('@/features/reports/ActivityReportPage'));
const JobAuditPage = lazy(() => import('@/features/reports/JobAuditPage'));
const DurationLogsPage = lazy(() => import('@/features/reports/DurationLogsPage'));
const PerformancePage = lazy(() => import('@/features/reports/PerformancePage'));
const TagsPerformancePage = lazy(() => import('@/features/reports/TagsPerformancePage'));
const DurationReportPage = lazy(() => import('@/features/reports/DurationReportPage'));
const StepDurationReportPage = lazy(() => import('@/features/reports/StepDurationReportPage'));
const TagsReportPage = lazy(() => import('@/features/reports/TagsReportPage'));
const PipelineReportPage = lazy(() => import('@/features/reports/PipelineReportPage'));
const WorkgroupPipelineReportPage = lazy(() => import('@/features/reports/WorkgroupPipelineReportPage'));
const JobOutcomeReportPage = lazy(() => import('@/features/reports/JobOutcomeReportPage'));
const TemplateAuditReportPage = lazy(() => import('@/features/reports/TemplateAuditReportPage'));
const NdRecordAuditReportPage = lazy(() => import('@/features/reports/NdRecordAuditReportPage'));
const TimeBreakdownReportPage = lazy(() => import('@/features/reports/TimeBreakdownReportPage'));
const AiUsageReportPage = lazy(() => import('@/features/reports/AiUsageReportPage'));
const AiActivityReportPage = lazy(() => import('@/features/reports/AiActivityReportPage'));
const JobStepApprovalsReportPage = lazy(() => import('@/features/reports/JobStepApprovalsReportPage'));
const JobStepBasedApprovalReportPage = lazy(() => import('@/features/reports/JobStepBasedApprovalReportPage'));
const PeopleChangesHistoryPage = lazy(() => import('@/features/people-changes/PeopleChangesHistoryPage'));
const PeopleChangesWizardPage = lazy(() => import('@/features/people-changes/PeopleChangesWizardPage'));

// Language Management
const LanguageTextsPage = lazy(() => import('@/features/language-texts/LanguageTextsPage'));

/**
 * Suspense wrapper for lazy-loaded components
 */
const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Spinner mode="centered" />}>{children}</Suspense>
);

/** Fullscreen spinner shown while the login page loads */
const LoginFallback = () => {
  const { t } = useTranslation();
  return <Spinner mode="fullscreen" text={t('Loading...', { defaultValue: 'Loading...' })} />;
};

/** Centered status text for the auth callback routes */
const AuthCallbackMessage = ({ kind }: { kind: 'login' | 'logout' }) => {
  const { t } = useTranslation();
  return (
    <div style={{ padding: '24px', textAlign: 'center' }}>
      {kind === 'login'
        ? t('Routes:ProcessingLogin', { defaultValue: 'Processing login...' })
        : t('Routes:LoggingOut', { defaultValue: 'Logging out...' })}
    </div>
  );
};


/**
 * Application router configuration
 */
const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthGuard>
        <AppShell>
          <Outlet />
        </AppShell>
      </AuthGuard>
    ),
    children: [
      {
        // Dashboard is the home page - redirect root to dashboard
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <SuspenseWrapper>
            <DashboardPage />
          </SuspenseWrapper>
        ),
        children: [
          // Client workspace tabs (rendered inside the client shell)
          {
            path: 'requests',
            element: (
              <SuspenseWrapper>
                <ClientMyRequestScreen />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'front-door',
            element: (
              <SuspenseWrapper>
                <ClientFrontDoorScreen />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'front-door/:questionnaireId',
            element: (
              <SuspenseWrapper>
                <ClientFrontDoorScreen />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'documents',
            element: (
              <SuspenseWrapper>
                <ClientDocumentsScreen />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'activity',
            element: (
              <SuspenseWrapper>
                <ClientActivityScreen />
              </SuspenseWrapper>
            ),
          },
          // Dynamic default — questionnaire tab or WIP
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <DashboardDefaultRedirect />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'wip',
            element: (
              <PermissionGuard permission="motr.Dashboard.Wip" fallback={<Navigate to={`/dashboard${window.location.search}`} replace />}>
                <SuspenseWrapper>
                  <WIPScreen />
                </SuspenseWrapper>
              </PermissionGuard>
            ),
          },
          {
            path: 'workload',
            element: (
              <PermissionGuard permission="motr.Dashboard.Workload" fallback={<Navigate to={`/dashboard${window.location.search}`} replace />}>
                <SuspenseWrapper>
                  <WorkloadScreen />
                </SuspenseWrapper>
              </PermissionGuard>
            ),
          },
          {
            path: 'summary',
            element: (
              <PermissionGuard permission="motr.Dashboard.Summary" fallback={<Navigate to={`/dashboard${window.location.search}`} replace />}>
                <SuspenseWrapper>
                  <SummaryScreen />
                </SuspenseWrapper>
              </PermissionGuard>
            ),
          },
          {
            // Legacy route - redirect to WIP
            path: 'todo',
            element: <Navigate to="/dashboard/wip" replace />,
          },
          {
            path: 'calendar',
            element: (
              <PermissionGuard
                permission="motr.Dashboard.Calendar"
                fallback={<Navigate to={`/dashboard${window.location.search}`} replace />}
              >
                <SuspenseWrapper>
                  <CalendarScreen />
                </SuspenseWrapper>
              </PermissionGuard>
            ),
          },
          {
            path: 'activities',
            element: (
              <PermissionGuard permission="motr.Dashboard.RecentActivity" fallback={<Navigate to={`/dashboard${window.location.search}`} replace />}>
                <SuspenseWrapper>
                  <ActivitiesScreen />
                </SuspenseWrapper>
              </PermissionGuard>
            ),
          },
          {
            path: 'q/:questionnaireId',
            element: (
              <SuspenseWrapper>
                <DashboardQuestionnaireEmbed />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'questionnaires',
            element: (
              <SuspenseWrapper>
                <QuestionnairesScreen />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'questionnaires/:questionnaireId',
            element: (
              <SuspenseWrapper>
                <QuestionnairesScreen />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      {
        // Legacy client-dashboard path — now lives under /dashboard
        path: 'client/*',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'ai-assistant',
        element: (
          <SuspenseWrapper>
            <AiAssistantPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'ai-providers',
        element: (
          <AiFeatureGuard>
            <SuspenseWrapper>
              <AiProvidersPage />
            </SuspenseWrapper>
          </AiFeatureGuard>
        ),
      },
      {
        path: 'ai-models',
        element: (
          <AiFeatureGuard>
            <SuspenseWrapper>
              <AiModelsPage />
            </SuspenseWrapper>
          </AiFeatureGuard>
        ),
      },
      {
        path: 'ai-tasks',
        element: (
          <AiFeatureGuard>
            <SuspenseWrapper>
              <AiTasksPage />
            </SuspenseWrapper>
          </AiFeatureGuard>
        ),
      },
      {
        path: 'ai-knowledge-base',
        element: (
          <AiFeatureGuard>
            <SuspenseWrapper>
              <KnowledgeBasePage />
            </SuspenseWrapper>
          </AiFeatureGuard>
        ),
      },
      {
        path: 'messages',
        element: (
          <SuspenseWrapper>
            <MessagesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/suggested-improvements/:type?',
        element: (
          <SuspenseWrapper>
            <SuggestedImprovementsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/risks',
        element: (
          <SuspenseWrapper>
            <JobRisksReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports-power',
        element: (
          <SuspenseWrapper>
            <PowerBIReportsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports-power/details',
        element: (
          <SuspenseWrapper>
            <PowerBIReportViewerPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/users',
        element: (
          <SuspenseWrapper>
            <UserReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/activity',
        element: (
          <PermissionGuard permission="motr.Reports.ActivityReport" fallback={<Navigate to="/dashboard" replace />}>
            <SuspenseWrapper>
              <ActivityReportPage />
            </SuspenseWrapper>
          </PermissionGuard>
        ),
      },
      {
        path: 'reports/job-history',
        element: (
          <SuspenseWrapper>
            <JobAuditPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/duration-logs',
        element: (
          <SuspenseWrapper>
            <DurationLogsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/system-performance-report',
        element: (
          <SuspenseWrapper>
            <PerformancePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/ai-usage',
        element: (
          <SuspenseWrapper>
            <AiUsageReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/ai-activity',
        element: (
          <SuspenseWrapper>
            <AiActivityReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/job-step-approvals',
        element: (
          <SuspenseWrapper>
            <JobStepApprovalsReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/job-step-based-approval',
        element: (
          <SuspenseWrapper>
            <JobStepBasedApprovalReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/tags-performance-report',
        element: (
          <SuspenseWrapper>
            <TagsPerformancePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/duration-report',
        element: (
          <SuspenseWrapper>
            <DurationReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/step-duration-report',
        element: (
          <SuspenseWrapper>
            <StepDurationReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/time-breakdown',
        element: (
          <SuspenseWrapper>
            <TimeBreakdownReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/tags-report',
        element: (
          <SuspenseWrapper>
            <TagsReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/pipeline',
        element: (
          <SuspenseWrapper>
            <PipelineReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/workgroup-pipeline',
        element: (
          <SuspenseWrapper>
            <WorkgroupPipelineReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/joboutcome',
        element: (
          <SuspenseWrapper>
            <JobOutcomeReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/template-audit',
        element: (
          <SuspenseWrapper>
            <TemplateAuditReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/new-record',
        element: (
          <SuspenseWrapper>
            <NdRecordAuditReportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reports/*',
        element: (
          <SuspenseWrapper>
            <ReportsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'people-changes',
        element: (
          <SuspenseWrapper>
            <PeopleChangesHistoryPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'people-changes/history/:id',
        element: (
          <SuspenseWrapper>
            <PeopleChangesWizardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'people-changes/wizard/:operation',
        element: (
          <SuspenseWrapper>
            <PeopleChangesWizardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'language-management/texts',
        element: (
          <SuspenseWrapper>
            <LanguageTextsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'superadmin/data-sync',
        element: (
          <RoleGuard role="SysAdmin" fallback={<Navigate to={`/dashboard${window.location.search}`} replace />}>
            <SuspenseWrapper>
              <DataSyncPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/tenants',
        element: (
          <SuspenseWrapper>
            <TenantsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admin/roles',
        element: (
          <SuspenseWrapper>
            <RolesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admin/process-templates',
        element: (
          <SuspenseWrapper>
            <ProcessTemplatesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admin/template-types',
        element: (
          <SuspenseWrapper>
            <TemplateTypesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admin/template-import',
        element: (
          <SuspenseWrapper>
            <TemplateImportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'my-account',
        element: (
          <SuspenseWrapper>
            <MyAccountPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'jobs/new-workflow',
        element: (
          <SuspenseWrapper>
            <StartNewJobPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'add/new-record',
        element: (
          <SuspenseWrapper>
            <AddNewRecordPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'jobs/new-service',
        element: (
          <SuspenseWrapper>
            <NewServiceRequestPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'my-approvals',
        element: (
          <SuspenseWrapper>
            <MyApprovalsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'authority-delegation',
        element: (
          <SuspenseWrapper>
            <AuthorityDelegationPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'people-workgroups',
        element: (
          <SuspenseWrapper>
            <PeopleWorkgroupsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'people-workgroups/import',
        element: (
          <SuspenseWrapper>
            <PermissionGuard permission="motr-functional.User.ImportUsers">
              <UserImportPage />
            </PermissionGuard>
          </SuspenseWrapper>
        ),
      },
      // Template management pages
      {
        path: 'manage-snippets',
        element: (
          <SuspenseWrapper>
            <SnippetsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'manage-key-maps',
        element: (
          <SuspenseWrapper>
            <KeyMapsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'manage-job-outcomes',
        element: (
          <SuspenseWrapper>
            <OutcomesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'manage-tags',
        element: (
          <SuspenseWrapper>
            <TagsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'mail-templates',
        element: (
          <SuspenseWrapper>
            <EmailTemplateConfigPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'manage-email-templates',
        element: (
          <SuspenseWrapper>
            <EmailTemplatesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'manage-document-templates',
        element: (
          <SuspenseWrapper>
            <DocumentTemplatesPage />
          </SuspenseWrapper>
        ),
      },
      // AI Skills Library (B1) — must precede admin/* catch-all
      {
        path: 'admin/skills',
        element: (
          <SuspenseWrapper>
            <AiFeatureGuard>
              <PermissionGuard permission="motr-functional.SkillsLibrary">
              <SkillsListPage />
            </PermissionGuard>
            </AiFeatureGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admin/skills/:id',
        element: (
          <SuspenseWrapper>
            <AiFeatureGuard>
              <PermissionGuard permission="motr-functional.SkillsLibrary">
              <SkillEditorPage />
            </PermissionGuard>
            </AiFeatureGuard>
          </SuspenseWrapper>
        ),
      },
      // MCP Servers (B2) — must precede admin/* catch-all
      {
        path: 'admin/mcp-servers',
        element: (
          <SuspenseWrapper>
            <AiFeatureGuard>
              <PermissionGuard permission="motr-functional.SkillsLibrary">
              <McpServersListPage />
            </PermissionGuard>
            </AiFeatureGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admin/mcp-servers/:id',
        element: (
          <SuspenseWrapper>
            <AiFeatureGuard>
              <PermissionGuard permission="motr-functional.SkillsLibrary">
              <McpServerEditorPage />
            </PermissionGuard>
            </AiFeatureGuard>
          </SuspenseWrapper>
        ),
      },
      // Packs (B3) — tenant enablement BEFORE :id catch
      {
        path: 'admin/packs/enablement',
        element: (
          <SuspenseWrapper>
            <AiFeatureGuard>
              <PermissionGuard permission="motr-functional.SkillsLibrary">
              <PackEnablementPage />
            </PermissionGuard>
            </AiFeatureGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admin/packs',
        element: (
          <SuspenseWrapper>
            <AiFeatureGuard>
              <PermissionGuard permission="motr-functional.SkillsLibrary">
              <PacksListPage />
            </PermissionGuard>
            </AiFeatureGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admin/packs/:id',
        element: (
          <SuspenseWrapper>
            <AiFeatureGuard>
              <PermissionGuard permission="motr-functional.SkillsLibrary">
              <PackEditorPage />
            </PermissionGuard>
            </AiFeatureGuard>
          </SuspenseWrapper>
        ),
      },
      {
        path: 'admin/*',
        element: (
          <SuspenseWrapper>
            <AdminPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'synced-data/:cabinetId/bulk-import',
        element: (
          <SuspenseWrapper>
            <BulkImportPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'synced-data/:cabinetId',
        element: (
          <SuspenseWrapper>
            <SyncedDataPage />
          </SuspenseWrapper>
        ),
      },
      {
        // More specific path first so it is not swallowed by 'entity-records'.
        path: 'entity-records/bulk-import',
        element: (
          <PermissionGuard permission="motr-functional.EntityRecords">
            <SuspenseWrapper>
              <EntityRecordBulkImportPage />
            </SuspenseWrapper>
          </PermissionGuard>
        ),
      },
      {
        path: 'entity-records',
        element: (
          <PermissionGuard permission="motr-functional.EntityRecords">
            <SuspenseWrapper>
              <EntityRecordsPage />
            </SuspenseWrapper>
          </PermissionGuard>
        ),
      },
      {
        path: 'entity-types',
        element: (
          <PermissionGuard permission="motr-functional.EntityTypes">
            <SuspenseWrapper>
              <EntityTypesPage />
            </SuspenseWrapper>
          </PermissionGuard>
        ),
      },
      {
        path: 'settings/*',
        element: (
          <SuspenseWrapper>
            <SettingsPage />
          </SuspenseWrapper>
        ),
      },
      // DocuSign signing callback route
      // Matches Angular's: wip-dashboard/j/:jobId/s/:stepId/e/:envelopId/:method
      // Backend constructs this URL as the return URL for DocuSign signing ceremony
      {
        path: 'wip-dashboard/j/:jobId/s/:stepId/e/:envelopId/:method',
        element: (
          <SuspenseWrapper>
            <DocuSignCallbackScreen />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: ROUTES.LOGIN,
    element: (
      <Suspense fallback={<LoginFallback />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.CALLBACK,
    element: <AuthCallbackMessage kind="login" />,
  },
  {
    path: ROUTES.LOGOUT_CALLBACK,
    element: <AuthCallbackMessage kind="logout" />,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

export const AppRouter = () => <RouterProvider router={router} future={{ v7_startTransition: true }} />;
